import type {Order} from '@wix/ambassador-ep-orders/types'
import {
  FeeUnit,
  PaymentStatus,
  type Reservation,
} from '@wix/ambassador-table-reservations-v1-reservation/types'

import {getFormattedPrice} from './getFormattedPrice'

type CancellationFeeState = {
  applicable?: boolean | null
  cancellationFeeApplied?: boolean | null
}

export const getCancellationFeeState = (reservation?: Reservation | null) =>
  reservation?.cancellationFeeState as CancellationFeeState | undefined

export const isCancellationFeeApplied = (reservation?: Reservation | null) =>
  getCancellationFeeState(reservation)?.cancellationFeeApplied === true

export const isReservationCharged = (reservation?: Reservation | null) => {
  const paymentStatus = reservation?.paymentStatus

  return paymentStatus === PaymentStatus.PAID || paymentStatus === PaymentStatus.PARTIALLY_PAID
}

export const getAppliedCancellationFeeAmount = (
  reservation?: Reservation | null,
  order?: Order | null,
  currency?: string | null,
) => {
  const orderTotal = order?.priceSummary?.total

  if (orderTotal?.formattedAmount && Number(orderTotal.amount) > 0) {
    return orderTotal.formattedAmount
  }

  if (orderTotal?.amount && Number(orderTotal.amount) > 0) {
    return formatFeeAmount(orderTotal.amount, order?.currency ?? currency)
  }

  const fee = reservation?.cancellationPolicy?.cancellationFee
  const partySize = reservation?.details?.partySize ?? 1

  if (!fee?.amount) {
    return undefined
  }

  const total = fee.unit === FeeUnit.PER_GUEST ? Number(fee.amount) * partySize : Number(fee.amount)

  if (!Number.isFinite(total)) {
    return fee.amount
  }

  return formatFeeAmount(String(total), order?.currency ?? currency)
}

const formatFeeAmount = (amount?: string | null, currency?: string | null) => {
  if (!amount) {
    return undefined
  }

  const numericAmount = Number(amount)

  if (!Number.isFinite(numericAmount) || !currency) {
    return amount
  }

  try {
    return getFormattedPrice(numericAmount, currency)
  } catch {
    return amount
  }
}
